import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/shared/hooks/use-toast";
import { usePresetFiles } from "./usePresetFiles";
import { useGalleryOperations } from "./useGalleryOperations";
import { useCreatePreset } from "./useCreatePreset";
import { useUpdatePreset } from "./useUpdatePreset";
import { useDeletePreset } from "./useDeletePreset";
import { presetApi } from "../api";
import type { GalleryItem } from "../types";
import { useCustomMutation } from "@/shared/hooks/useCustomMutation";
import { TagsService } from "@/features/tags/service/tagService";
import CategoryService from "@/features/categories/service/categoryService";
import { useTranslation } from "@/shared/i18n";
import type { TranslationItem } from "@/shared/widgets/translations-field";
import { useQueryClient } from "@tanstack/react-query";
import { presetKeys } from "./preset.keys";

type PresetData = {
  id?: string;
  packId?: string;
  name?: string;
  cover?: string;
  lut?: string;
  isFree?: boolean;
  published?: boolean;
  tags?: string[] | string;
  translations?: { languageCode: string; about: string }[];
  categories?: { id: string }[];
  CategoriesPresets?: { categoryId: string }[];
};

export function usePresetForm(
  presetData: PresetData | null | undefined,
  isEditing: boolean,
  packIdFromPage: string | undefined
) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation('presets');
  const queryClient = useQueryClient();
  const [name, setName] = useState(presetData?.name ?? "");
  
  // Converte translations antigas para o novo formato
  const initialTranslations: TranslationItem[] = presetData?.translations?.map(t => ({
    languageCode: t.languageCode as 'PT_BR' | 'EN' | 'ES',
    description: t.about
  })) ?? [];
  
  const [translations, setTranslations] = useState<TranslationItem[]>(
    initialTranslations.length > 0 ? initialTranslations : [{ languageCode: 'PT_BR', description: '' }]
  );
  const [isFree, setIsFree] = useState(presetData?.isFree ?? false);
  const [tags, setTags] = useState(
    Array.isArray(presetData?.tags) ? presetData.tags.join(", ") : (presetData?.tags as string) ?? ""
  );
  const [published, setPublished] = useState(presetData?.published ?? false);
  const initialCategoryIds =
    presetData?.categories?.map((c) => c.id) ?? presetData?.CategoriesPresets?.map((cp) => cp.categoryId) ?? [];
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>(initialCategoryIds);
  const [originalCategoryIds, setOriginalCategoryIds] = useState<string[]>(initialCategoryIds);

  useEffect(() => {
    if (presetData) {
      setName(presetData.name ?? "");
      const updatedTranslations = presetData.translations?.map(t => ({
        languageCode: t.languageCode as 'PT_BR' | 'EN' | 'ES',
        description: t.about
      })) ?? [];
      setTranslations(updatedTranslations.length > 0 ? updatedTranslations : [{ languageCode: 'PT_BR', description: '' }]);
      setIsFree(presetData.isFree ?? false);
      setTags(Array.isArray(presetData.tags) ? presetData.tags.join(", ") : (presetData.tags as string) ?? "");
      setPublished(presetData.published ?? false);
      const ids = presetData.categories?.map((c) => c.id) ?? presetData.CategoriesPresets?.map((cp) => cp.categoryId) ?? [];
      setSelectedCategoryIds(ids);
      setOriginalCategoryIds(ids);
    }
  }, [presetData]);

  const files = usePresetFiles({
    initialCover: presetData?.cover ?? null,
    initialLut: presetData?.lut ?? null,
  });
  const galleryOps = useGalleryOperations(presetData?.id ?? null, isEditing);

  const [isSubmittingForm, setIsSubmittingForm] = useState(false);
  const { createPreset, mutationResult: createPresetResult } = useCreatePreset({ onSuccess: () => {} });
  const { updatePreset, mutationResult: updatePresetResult } = useUpdatePreset({ onSuccess: () => {} });
  const { deletePreset, mutationResult: deletePresetResult } = useDeletePreset();
  const submitting =
    isSubmittingForm ||
    createPresetResult.isPending ||
    updatePresetResult.isPending ||
    galleryOps.isUploadingGallery;

  const linkTagMutation = useCustomMutation({
    serviceInstance: TagsService.Instance,
    selectAction: (s) => s.linkTagToPreset,
  });
  const associateCategoryMutation = useCustomMutation({
    serviceInstance: CategoryService.Instance,
    selectAction: (s) => s.associatePresetToCategory,
  });
  const removeCategoryMutation = useCustomMutation({
    serviceInstance: CategoryService.Instance,
    selectAction: (s) => s.removePresetFromCategory,
  });

  const handleSubmit = async (
    packId: string,
    presetId: string | null,
    coverFile: File | null,
    lutFile: File | null,
    uploadGalleryImages: (presetId: string) => Promise<GalleryItem[]>
  ): Promise<string | number> => {
    try {
      if (!packId) {
        toast({ title: t('error'), description: t('packIdNotFound'), variant: "destructive" });
        return 0;
      }
      if (!name.trim()) {
        toast({ title: t('error'), description: t('nameIsRequired'), variant: "destructive" });
        return 0;
      }
      if (!isEditing) {
        if (!coverFile || !(coverFile instanceof File)) {
          toast({ title: t('coverIsRequiredTitle'), description: t('coverIsRequired'), variant: "destructive" });
          return 0;
        }
        if (!lutFile || !(lutFile instanceof File)) {
          toast({ title: t('lutIsRequiredTitle'), description: t('lutIsRequired'), variant: "destructive" });
          return 0;
        }
      }

      const payload = {
        packId,
        name: name.trim(),
        isFree,
        isBlackAndWhite: false,
        published,
        translations: translations
          .filter((t) => t.description?.trim())
          .map((t) => ({
            languageCode: t.languageCode as 'PT_BR' | 'EN' | 'ES',
            about: t.description.trim(),
          })),
      };

      if (isEditing && presetId) {
        if (coverFile instanceof File) await presetApi.uploadCover(presetId, coverFile);
        if (lutFile instanceof File) await presetApi.uploadLut(presetId, lutFile);
        await updatePreset(presetId, payload);
        await uploadGalleryImages(presetId);
        return presetId;
      }

      const data = await createPreset(payload);
      const newId = data?.id;
      if (!newId) {
        toast({ title: t('error'), description: t('presetCreatedNoId'), variant: "destructive" });
        return 0;
      }
      await Promise.all([
        presetApi.uploadCover(newId, coverFile!),
        presetApi.uploadLut(newId, lutFile!),
      ]);
      await uploadGalleryImages(newId);
      if (tags?.trim()) {
        const tagIds = tags.split(",").map((t) => t.trim()).filter(Boolean);
        for (const tagId of tagIds) {
          try {
            await linkTagMutation.mutateAsync({ tagId, presetId: newId });
          } catch {
            toast({ title: t('warningTitle'), description: t('errorLinkingTags'), variant: "destructive" });
          }
        }
      }
      return newId;
    } catch (err) {
      toast({
        title: t('errorSaving'),
        description: err instanceof Error ? err.message : t('errorSavingDescription'),
        variant: "destructive",
      });
      return 0;
    }
  };

  const handleCategoryAssociation = async (presetId: string) => {
    const toRemove = originalCategoryIds.filter((id) => !selectedCategoryIds.includes(id));
    const toAdd = selectedCategoryIds.filter((id) => !originalCategoryIds.includes(id));
    await Promise.all([
      ...toRemove.map((categoryId) => removeCategoryMutation.mutateAsync({ categoryId, presetId })),
      ...toAdd.map((categoryId) => associateCategoryMutation.mutateAsync({ categoryId, presetId })),
    ]);
  };

  const onSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setIsSubmittingForm(true);
    try {
      const packId = packIdFromPage ?? presetData?.packId ?? "";
      const result = await handleSubmit(
        packId,
        presetData?.id ?? null,
        files.imageFile,
        files.lutFile,
        galleryOps.uploadGalleryImages
      );
      if (isEditing) await galleryOps.commitGalleryDeletions();
      if (result && result !== 0) {
        const presetId = typeof result === "string" ? result : presetData?.id;
        if (presetId) {
          try {
            await handleCategoryAssociation(presetId);
          } catch (error) {
            console.error(error);
            toast({ title: t('warningTitle'), description: t('errorProcessingCategories'), variant: "destructive" });
          }
          
          // Invalida o cache do preset para forçar reload dos dados atualizados
          queryClient.invalidateQueries({ queryKey: presetKeys.detail(presetId) });
          queryClient.invalidateQueries({ queryKey: presetKeys.all });
        }
        navigate(-1);
      }
    } finally {
      setIsSubmittingForm(false);
    }
  };

  return {
    name,
    setName,
    translations,
    setTranslations,
    tags,
    setTags,
    published,
    setPublished,
    selectedCategoryIds,
    setSelectedCategoryIds,
    imagePreview: files.imagePreview,
    imageFile: files.imageFile,
    imageError: files.imageError,
    handleImageChange: files.handleImageChange,
    setImagePreview: files.setImagePreview,
    setImageFile: files.setImageFile,
    lutFilename: files.lutFilename,
    lutFile: files.lutFile,
    lutPreview: files.lutUrl,
    lutError: files.lutError,
    handleLutChange: files.handleLutChange,
    setLutFilename: files.setLutFilename,
    setLutFile: files.setLutFile,
    setLutUrl: files.setLutUrl,
    ...galleryOps,
    onSubmit,
    submitting,
    isFree,
    handleIsFreeChange: setIsFree,
    createPresetMutation: createPresetResult,
    updatePresetMutation: updatePresetResult,
    deletePresetMutation: deletePresetResult,
    associateCategoryMutation,
    removeCategoryMutation,
  };
}
